import {contentEndpoint, element, icon} from './remote-data';
import {cachedPublicJSON} from './session-data';
import {localized} from './api-data';
import {workOptions, workItems, workDate, workDay, workKey, workHeadline} from './work-data';
import {currentLocale, translate} from './preferences';

function countLabel(count, mode, locale) {
    if (locale === 'zh-TW') return mode === 'Detail' ? `${count} 筆提交` : `${count} 個專案`;
    return `${count} ${mode === 'Detail' ? 'commit' : 'project'}${count === 1 ? '' : 's'}`;
}
function activitySection(section, signal, restored) {
    const {mode, limit} = workOptions(section.dataset.mode, section.dataset.limit);
    const detail = mode === 'Detail';
    const list = section.querySelector('[data-activity-list]');
    const viewport = section.querySelector('[data-activity-viewport]');
    const count = section.querySelector('[data-activity-count]');
    const period = section.querySelector('[data-activity-period]');
    const retry = section.querySelector('[data-activity-retry]');
    const desktop = window.matchMedia('(min-width: 901px)');
    let items = restored?.items ?? null, failed = restored?.failed ?? false;
    function updateViewport() {
        const scrollable = detail && desktop.matches && viewport.scrollHeight > viewport.clientHeight;
        section.classList.toggle('activity-scrollable', scrollable);
        if (scrollable) viewport.tabIndex = 0;
        else viewport.removeAttribute('tabindex');
    }
    function entry(item, locale, continuation = false) {
        const copy = localized(item, locale);
        const link = element('a', 'activity-entry');
        link.href = item.url; link.target = '_blank'; link.rel = 'noopener noreferrer'; link.lang = locale;
        link.dataset.workKey = workKey(item, mode);
        const timestamp = workDate(item, mode);
        const value = new Date(timestamp);
        const date = element('time', `activity-date${continuation ? ' activity-date-continuation' : ''}`);
        date.dateTime = timestamp;
        date.setAttribute('aria-label', new Intl.DateTimeFormat(locale, {dateStyle: 'full', timeZone: 'UTC'}).format(value));
        date.append(element('span', '', new Intl.DateTimeFormat('en', {day: '2-digit', timeZone: 'UTC'}).format(value)),
            element('small', '', new Intl.DateTimeFormat(locale, {month: 'short', timeZone: 'UTC'}).format(value).toUpperCase()));
        const content = element('div', 'activity-content');
        const repo = element('div', 'activity-repo');
        const [owner, name] = item.repo.split('/');
        const label = owner.toLowerCase() === section.dataset.githubUsername?.toLowerCase() ? name : item.repo;
        repo.append(element('span', '', label), icon(section.dataset.externalIcon, 14));
        content.append(repo, element('p', 'activity-headline', workHeadline(copy.headline, item.repo, mode)));
        if (!detail) {
            const stats = item.stats || {};
            const positive = value => Number.isInteger(value) && value > 0 ? value : 0;
            const commits = positive(stats.commits), prs = positive(stats.pull_requests);
            const meta = [commits && countLabel(commits, 'Detail', locale), prs && `${prs} PR${prs === 1 ? '' : 's'}`].filter(Boolean).join(' · ');
            if (meta) content.append(element('p', 'activity-meta', meta));
        }
        link.append(date, content);
        return link;
    }
    function render() {
        if (!items) return;
        const scrollTop = viewport.scrollTop;
        const focusedKey = document.activeElement?.closest('[data-work-key]')?.dataset.workKey;
        const locale = currentLocale();
        const fragment = document.createDocumentFragment();
        let day, group;
        items.forEach(item => {
            if (!detail) { fragment.append(entry(item, locale)); return; }
            // Sort globally before grouping: one UTC day may contain several repos.
            const nextDay = workDay(workDate(item, mode));
            const continuation = nextDay === day;
            if (!continuation) {
                group = element('div', 'activity-day');
                group.dataset.workDay = nextDay;
                fragment.append(group); day = nextDay;
            }
            group.append(entry(item, locale, continuation));
        });
        if (!items.length) {
            fragment.append(element('p', 'activity-fallback', translate(failed ? 'activityError' : 'noActivity')));
        }
        list.replaceChildren(fragment);
        retry.hidden = !failed;
        count.hidden = failed;
        count.textContent = countLabel(items.length, mode, locale);
        if (period) {
            period.hidden = !items.length;
            if (items.length) {
                const formatter = new Intl.DateTimeFormat(locale === 'en-US' ? 'en-GB' : locale, {day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'});
                section.querySelector('[data-activity-range]').textContent = formatter.formatRange(new Date(workDate(items.at(-1), mode)), new Date(workDate(items[0], mode)));
                section.querySelector('[data-activity-shown]').textContent = locale === 'zh-TW' ? `顯示 ${items.length} 筆提交` : `${countLabel(items.length, mode, locale)} shown`;
            }
        }
        viewport.scrollTop = scrollTop;
        if (focusedKey) [...list.querySelectorAll('[data-work-key]')].find(link => link.dataset.workKey === focusedKey)?.focus({preventScroll: true});
        updateViewport();
    }
    async function load() {
        retry.disabled = true;
        viewport.setAttribute('aria-busy', 'true');
        section.dataset.loading = items === null ? 'initial' : 'more';
        try {
            const url = contentEndpoint(section.dataset.api, '/api/v1/work-status');
            url.searchParams.set('mode', mode); url.searchParams.set('limit', limit);
            const response = await cachedPublicJSON(url, value => ({items: workItems(value, {mode, limit}), status: value.status}), {cache: 'no-store', signal});
            if (signal?.aborted) return;
            items = response.items;
            section.dataset.activityStatus = response.status;
            failed = false;
        } catch (_) {
            if (signal?.aborted) return;
            items = []; failed = true;
            section.dataset.activityStatus = 'unavailable';
        }
        retry.disabled = false;
        render();
        viewport.setAttribute('aria-busy', 'false');
        section.dataset.loading = 'false';
    }
    retry.addEventListener('click', load, {signal});
    window.addEventListener('tiwb:language', render, {signal});
    desktop.addEventListener('change', updateViewport, {signal});
    const observer = new ResizeObserver(updateViewport);
    observer.observe(list);
    signal?.addEventListener('abort', () => observer.disconnect(), {once: true});
    if (restored?.items) {
        section.dataset.activityStatus = restored.status;
        section.dataset.loading = 'false';
        viewport.setAttribute('aria-busy', 'false');
        render();
    }
    return {ready: restored?.items ? Promise.resolve() : load(), snapshot: () => ({items, failed, status: section.dataset.activityStatus})};
}
export default function activity(root = document, signal, restored = []) {
    const sections = [...root.querySelectorAll('[data-recent-work]')].map((section, index) => activitySection(section, signal, restored[index]));
    return {ready: Promise.all(sections.map(section => section.ready)), snapshot: () => sections.map(section => section.snapshot())};
}
